import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import { format } from 'date-fns';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { getRule, ruleSyncLog, syncNow } from '@/api/endpoints';
import type { SyncLogEntry, SyncRule } from '@/api/types';
import { PageHeader } from '@/components/PageHeader';
import { ErrorsSparkline } from '@/components/rules/ErrorsSparkline';

export function RuleDetailPage() {
  const { ruleId } = useParams();
  const id = Number(ruleId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const rule = useQuery<SyncRule>({
    queryKey: ['rules', id],
    queryFn: () => getRule(id),
    enabled: Number.isFinite(id),
  });
  const log = useQuery<SyncLogEntry[]>({
    queryKey: ['rules', id, 'sync-log'],
    queryFn: () => ruleSyncLog(id),
    enabled: Number.isFinite(id),
  });

  const trigger = useMutation({
    mutationFn: () => syncNow(id),
    onSuccess: () => {
      enqueueSnackbar('Sync enqueued. Refresh the log in a moment.', { variant: 'info' });
      // Soft-refetch after 1.5s; the worker isn't fast enough to land within
      // a click. Users can also pull to refresh.
      setTimeout(() => queryClient.invalidateQueries({ queryKey: ['rules', id, 'sync-log'] }), 1500);
    },
    onError: (err) => enqueueSnackbar(err.message, { variant: 'error' }),
  });

  if (!Number.isFinite(id)) {
    return (
      <Container>
        <Typography>Invalid rule id.</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" disableGutters>
      <PageHeader
        title={rule.data?.name ?? 'Rule'}
        subtitle={
          rule.data
            ? `${rule.data.detection_mode === 'webhook' ? 'Webhook' : `Poll every ${rule.data.poll_interval_s}s`} · replaces titles with “${rule.data.replacement_title}”`
            : undefined
        }
        actions={
          <>
            <Button onClick={() => navigate('/rules')}>Back</Button>
            <Button
              variant="contained"
              startIcon={<SyncIcon />}
              onClick={() => trigger.mutate()}
              disabled={trigger.isPending}
            >
              Sync now
            </Button>
          </>
        }
      />

      {(rule.isLoading || log.isLoading) && (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress size={28} />
        </Stack>
      )}

      {rule.isSuccess && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="subtitle1">Errors per day</Typography>
              <Chip
                size="small"
                color={rule.data.paused ? 'default' : 'success'}
                label={rule.data.paused ? 'Paused' : 'Active'}
              />
            </Stack>
            <ErrorsSparkline log={log.data ?? []} />
          </CardContent>
        </Card>
      )}

      {log.isSuccess && (
        <TableContainer component={Card} variant="outlined">
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle1">Recent runs</Typography>
            <Typography variant="caption" color="text.secondary">
              Counters only — no event content is logged.
            </Typography>
          </Box>
          <Divider />
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Run at</TableCell>
                <TableCell align="right">+ Added</TableCell>
                <TableCell align="right">~ Modified</TableCell>
                <TableCell align="right">− Deleted</TableCell>
                <TableCell align="right">Errors</TableCell>
                <TableCell align="right">Duration</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {log.data.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No sync runs yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {log.data.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{format(new Date(row.run_at), 'PPpp')}</TableCell>
                  <TableCell align="right">{row.events_added}</TableCell>
                  <TableCell align="right">{row.events_modified}</TableCell>
                  <TableCell align="right">{row.events_deleted}</TableCell>
                  <TableCell
                    align="right"
                    sx={{ color: row.errors > 0 ? 'error.main' : undefined }}
                  >
                    {row.errors}
                  </TableCell>
                  <TableCell align="right">{(row.duration_ms / 1000).toFixed(2)}s</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
}
